/**
 * CRM Service API
 * Client relationship management, leads, opportunities, and communications
 */

import { createAPIClient } from '../client'
import { API_CONFIG } from '../config'

const client = createAPIClient(API_CONFIG.CRM.baseURL, API_CONFIG.CRM.timeout)

export const crmService = {
  // ==================== Engagement ====================
  //
  // Through the shared client so the bearer token is attached. `ClientDetail` called these with a
  // bare `fetch()`, which carries no session — both 401'd on every load, and the page quietly
  // rendered "Health Score 0" and "0 interactions" as though those were the real numbers.

  /** Client health score. */
  getClientHealthScore: async (clientId) => {
    const response = await client.get(`/engagement/clients/${clientId}/health-score`)
    return response.data
  },

  /** Client engagement metrics. */
  getClientEngagementMetrics: async (clientId) => {
    const response = await client.get(`/engagement/clients/${clientId}/metrics`)
    return response.data
  },

  // ==================== Clients ====================

  /**
   * Search clients with filters and pagination
   * @param {object} params - Query parameters (search, status, assigned_to, is_vip, skip, limit, sort_by, sort_order)
   * @returns {Promise} ClientListResponse with items and pagination
   */
  searchClients: async (params = {}) => {
    const response = await client.get('/clients/search', { params })
    return response.data
  },

  /**
   * Get client statistics for dashboard
   * @returns {Promise} ClientStatistics
   */
  getClientStatistics: async () => {
    const response = await client.get('/clients/statistics')
    return response.data
  },

  /**
   * Get client by ID
   * @param {number} id - Client ID
   * @returns {Promise} Client data
   */
  getClient: async (id) => {
    const response = await client.get(`/clients/${id}`)
    return response.data
  },

  /**
   * Get client by client number
   * @param {string} clientNumber - Client number (CLI-YYYY-NNNN)
   * @returns {Promise} Client data
   */
  getClientByNumber: async (clientNumber) => {
    const response = await client.get(`/clients/number/${clientNumber}`)
    return response.data
  },

  /**
   * Get client with embedded contacts
   * @param {number} id - Client ID
   * @returns {Promise} ClientWithContacts
   */
  getClientWithContacts: async (id) => {
    const response = await client.get(`/clients/${id}/with-contacts`)
    return response.data
  },

  /**
   * Create new client
   * @param {object} clientData - Client data
   * @returns {Promise} Created client with auto-generated client_number
   */
  createClient: async (clientData) => {
    const response = await client.post('/clients/', clientData)
    return response.data
  },

  /**
   * Update client
   * @param {number} id - Client ID
   * @param {object} clientData - Updated client data
   * @returns {Promise} Updated client
   */
  updateClient: async (id, clientData) => {
    const response = await client.put(`/clients/${id}`, clientData)
    return response.data
  },

  /**
   * Soft delete client
   * @param {number} id - Client ID
   * @returns {Promise}
   */
  deleteClient: async (id) => {
    const response = await client.delete(`/clients/${id}`)
    return response.data
  },

  /**
   * Restore soft-deleted client
   * @param {number} id - Client ID
   * @returns {Promise} Restored client
   */
  restoreClient: async (id) => {
    const response = await client.post(`/clients/${id}/restore`)
    return response.data
  },

  /**
   * Recalculate client metrics (health score, lifetime value)
   * @param {number} id - Client ID
   * @returns {Promise} Updated client
   */
  updateClientMetrics: async (id) => {
    const response = await client.post(`/clients/${id}/update-metrics`)
    return response.data
  },

  // ==================== Client Contacts ====================

  /**
   * Get all contacts for a client
   * @param {number} clientId - Client ID
   * @returns {Promise} Array of contacts
   */
  getClientContacts: async (clientId) => {
    const response = await client.get(`/clients/${clientId}/contacts`)
    return response.data
  },

  /**
   * Get primary contact for a client
   * @param {number} clientId - Client ID
   * @returns {Promise} Primary contact or null
   */
  getPrimaryContact: async (clientId) => {
    const response = await client.get(`/clients/${clientId}/contacts/primary`)
    return response.data
  },

  /**
   * Get contact by ID
   * @param {number} id - Contact ID
   * @returns {Promise} Contact data
   */
  getContact: async (id) => {
    const response = await client.get(`/clients/contacts/${id}`)
    return response.data
  },

  /**
   * Create contact for client
   * @param {number} clientId - Client ID
   * @param {object} contactData - Contact data
   * @returns {Promise} Created contact
   */
  createContact: async (clientId, contactData) => {
    const response = await client.post(`/clients/${clientId}/contacts`, contactData)
    return response.data
  },

  /**
   * Update contact
   * @param {number} id - Contact ID
   * @param {object} contactData - Updated contact data
   * @returns {Promise} Updated contact
   */
  updateContact: async (id, contactData) => {
    const response = await client.put(`/clients/contacts/${id}`, contactData)
    return response.data
  },

  /**
   * Delete contact
   * @param {number} id - Contact ID
   * @returns {Promise}
   */
  deleteContact: async (id) => {
    const response = await client.delete(`/clients/contacts/${id}`)
    return response.data
  },

  // ==================== Leads ====================

  /**
   * Search leads with filters and pagination
   * @param {object} params - Query parameters (search, status, source, assigned_to, qualified_only, skip, limit)
   * @returns {Promise} LeadListResponse
   */
  searchLeads: async (params = {}) => {
    const response = await client.get('/leads/search', { params })
    return response.data
  },

  /**
   * Get lead by ID
   * @param {number} id - Lead ID
   * @returns {Promise} Lead data
   */
  getLead: async (id) => {
    const response = await client.get(`/leads/${id}`)
    return response.data
  },

  /**
   * Create new lead
   * @param {object} leadData - Lead data
   * @returns {Promise} Created lead
   */
  createLead: async (leadData) => {
    const response = await client.post('/leads/', leadData)
    return response.data
  },

  /**
   * Update lead
   * @param {number} id - Lead ID
   * @param {object} leadData - Updated lead data
   * @returns {Promise} Updated lead
   */
  updateLead: async (id, leadData) => {
    const response = await client.put(`/leads/${id}`, leadData)
    return response.data
  },

  /**
   * Delete lead
   * @param {number} id - Lead ID
   * @returns {Promise}
   */
  deleteLead: async (id) => {
    const response = await client.delete(`/leads/${id}`)
    return response.data
  },

  /**
   * Convert lead to client (optionally create opportunity)
   * @param {number} id - Lead ID
   * @param {object} conversionData - Conversion options (create_opportunity, opportunity_data)
   * @returns {Promise} { client, opportunity? }
   */
  convertLead: async (id, conversionData = {}) => {
    const response = await client.post(`/leads/${id}/convert`, conversionData)
    return response.data
  },

  // ==================== Opportunities ====================

  /**
   * Search opportunities with filters and pagination
   * @param {object} params - Query parameters (search, stage, client_id, project_type, assigned_to, skip, limit)
   * @returns {Promise} OpportunityListResponse
   */
  searchOpportunities: async (params = {}) => {
    const response = await client.get('/opportunities/search', { params })
    return response.data
  },

  /**
   * Get pipeline value summary
   * @returns {Promise} { total_value, by_stage: { stage: value } }
   */
  getPipelineValue: async () => {
    const response = await client.get('/opportunities/pipeline')
    return response.data
  },

  /**
   * Get opportunity by ID
   * @param {number} id - Opportunity ID
   * @returns {Promise} Opportunity data
   */
  getOpportunity: async (id) => {
    const response = await client.get(`/opportunities/${id}`)
    return response.data
  },

  /**
   * Get opportunity by opportunity number
   * @param {string} oppNumber - Opportunity number (OPP-YYYY-NNNN)
   * @returns {Promise} Opportunity data
   */
  getOpportunityByNumber: async (oppNumber) => {
    const response = await client.get(`/opportunities/number/${oppNumber}`)
    return response.data
  },

  /**
   * Get all opportunities for a client
   * @param {number} clientId - Client ID
   * @returns {Promise} Array of opportunities
   */
  getClientOpportunities: async (clientId) => {
    const response = await client.get(`/opportunities/client/${clientId}/opportunities`)
    return response.data
  },

  /**
   * Get all projects for a client (cross-module integration)
   * @param {number} clientId - Client ID
   * @returns {Promise} Array of projects from Projects module
   */
  getClientProjects: async (clientId) => {
    const response = await client.get(`/clients/${clientId}/projects`)
    return response.data
  },

  /**
   * Create new opportunity
   * @param {object} opportunityData - Opportunity data
   * @returns {Promise} Created opportunity with auto-generated opportunity_number
   */
  // NOTE: the trailing slash on these collection POSTs is REQUIRED, not cosmetic. The backend routes
  // are declared as post("/"), so a POST without the slash triggers FastAPI's slash-redirect - which
  // responds 307 to the INTERNAL rewritten path (/api/v1/...). nginx only routes /api/crm/..., not
  // /api/v1/..., so the browser's redirected request 404/405s. Posting to the slash directly avoids
  // the redirect entirely. Do not "clean up" the trailing slash.
  createOpportunity: async (opportunityData) => {
    const response = await client.post('/opportunities/', opportunityData)
    return response.data
  },

  /**
   * Update opportunity
   * @param {number} id - Opportunity ID
   * @param {object} opportunityData - Updated opportunity data
   * @returns {Promise} Updated opportunity
   */
  updateOpportunity: async (id, opportunityData) => {
    const response = await client.put(`/opportunities/${id}`, opportunityData)
    return response.data
  },

  /**
   * Delete opportunity
   * @param {number} id - Opportunity ID
   * @returns {Promise}
   */
  deleteOpportunity: async (id) => {
    const response = await client.delete(`/opportunities/${id}`)
    return response.data
  },

  /**
   * Link an opportunity to an existing project (bidirectional)
   * @param {number} opportunityId - Opportunity ID
   * @param {string} projectId - Project ID (e.g. PROJ-ABC123)
   * @returns {Promise} Updated opportunity
   */
  linkProjectToOpportunity: async (opportunityId, projectId) => {
    const response = await client.post(`/opportunities/${opportunityId}/link-project`, { project_id: projectId })
    return response.data
  },

  /**
   * Unlink an opportunity from its linked project (bidirectional)
   * @param {number} opportunityId - Opportunity ID
   * @returns {Promise} Updated opportunity
   */
  unlinkProjectFromOpportunity: async (opportunityId) => {
    const response = await client.post(`/opportunities/${opportunityId}/unlink-project`)
    return response.data
  },

  // ==================== Communications ====================

  /**
   * Search communications with filters
   * @param {object} params - Query parameters (client_id, contact_id, type, start_date, end_date, skip, limit)
   * @returns {Promise} CommunicationListResponse
   */
  searchCommunications: async (params = {}) => {
    const response = await client.get('/communications/search', { params })
    return response.data
  },

  /**
   * Get recent communications for client
   * @param {number} clientId - Client ID
   * @param {number} limit - Number of recent communications (default 10)
   * @returns {Promise} Array of communications
   */
  getRecentCommunications: async (clientId, limit = 10) => {
    const response = await client.get(`/communications/client/${clientId}/recent`, { params: { limit } })
    return response.data
  },

  /**
   * Get communication by ID
   * @param {number} id - Communication ID
   * @returns {Promise} Communication data
   */
  getCommunication: async (id) => {
    const response = await client.get(`/communications/${id}`)
    return response.data
  },

  /**
   * Log new communication (auto-updates client.last_contact_date)
   * @param {object} communicationData - Communication data
   * @returns {Promise} Created communication
   */
  createCommunication: async (communicationData) => {
    const response = await client.post('/communications/', communicationData)
    return response.data
  },

  /**
   * Update communication
   * @param {number} id - Communication ID
   * @param {object} communicationData - Updated communication data
   * @returns {Promise} Updated communication
   */
  updateCommunication: async (id, communicationData) => {
    const response = await client.put(`/communications/${id}`, communicationData)
    return response.data
  },

  // ==================== Documents ====================

  /**
   * Search documents with filters
   * @param {object} params - Query parameters (client_id, type, is_expired, skip, limit)
   * @returns {Promise} DocumentListResponse
   */
  searchDocuments: async (params = {}) => {
    const response = await client.get('/documents/search', { params })
    return response.data
  },

  /**
   * Get document by ID
   * @param {number} id - Document ID
   * @returns {Promise} Document data
   */
  getDocument: async (id) => {
    const response = await client.get(`/documents/${id}`)
    return response.data
  },

  /**
   * Upload new document
   * @param {object} documentData - Document data
   * @returns {Promise} Created document
   */
  createDocument: async (documentData) => {
    const response = await client.post('/documents/', documentData)
    return response.data
  },

  /**
   * Update document metadata
   * @param {number} id - Document ID
   * @param {object} documentData - Updated document data
   * @returns {Promise} Updated document
   */
  updateDocument: async (id, documentData) => {
    const response = await client.put(`/documents/${id}`, documentData)
    return response.data
  },

  /**
   * Delete document
   * @param {number} id - Document ID
   * @returns {Promise}
   */
  /**
   * Upload a client document.
   *
   * THROUGH THE CLIENT, not a bare `fetch`. `DocumentUploadForm` posted with `fetch()`, which
   * carries no Authorization header — so every upload 401'd and the modal showed the raw server
   * detail. This file already records fixing exactly that for the engagement calls; the same defect
   * had been left in the one place that moves a FILE.
   *
   * `Content-Type: multipart/form-data` is set explicitly, matching `hrService.importExcel` — the
   * client's default is application/json and would corrupt the body.
   */
  uploadDocument: async (params, formData) => {
    const query = new URLSearchParams(params).toString()
    const response = await client.post(`/documents/upload?${query}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  deleteDocument: async (id) => {
    const response = await client.delete(`/documents/${id}`)
    return response.data
  },

  // ==================== Client Notes ====================

  /**
   * Get all notes for a client
   * @param {number} clientId - Client ID
   * @param {object} params - Query parameters (note_type, is_pinned)
   * @returns {Promise} Array of notes
   */
  getClientNotes: async (clientId, params = {}) => {
    const response = await client.get(`/notes/client/${clientId}`, { params })
    return response.data
  },

  /**
   * Get note by ID
   * @param {number} id - Note ID
   * @returns {Promise} Note data
   */
  getNote: async (id) => {
    const response = await client.get(`/notes/${id}`)
    return response.data
  },

  /**
   * Create note for client
   * @param {object} noteData - Note data
   * @returns {Promise} Created note
   */
  createNote: async (noteData) => {
    const response = await client.post('/notes/', noteData)
    return response.data
  },

  /**
   * Update note
   * @param {number} id - Note ID
   * @param {object} noteData - Updated note data
   * @returns {Promise} Updated note
   */
  updateNote: async (id, noteData) => {
    const response = await client.put(`/notes/${id}`, noteData)
    return response.data
  },

  /**
   * Delete note
   * @param {number} id - Note ID
   * @returns {Promise}
   */
  deleteNote: async (id) => {
    const response = await client.delete(`/notes/${id}`)
    return response.data
  },

  // ==================== Customer Equipment (asset register + PM program) ====================

  /** List a customer's asset register. */
  getClientEquipment: async (clientId, params = {}) => {
    const response = await client.get(`/clients/${clientId}/equipment`, { params })
    return response.data
  },

  /** Distinct plant areas for a customer (for the register's area filter). */
  getClientEquipmentAreas: async (clientId) => {
    const response = await client.get(`/clients/${clientId}/equipment/areas`)
    return response.data
  },

  /** Edit one asset. */
  updateClientEquipment: async (clientId, equipmentId, body) => {
    const response = await client.put(`/clients/${clientId}/equipment/${equipmentId}`, body)
    return response.data
  },

  /** PM program: owed vs done for a month (YYYY-MM), by area. */
  getPmStatus: async (clientId, month) => {
    const response = await client.get(`/clients/${clientId}/pm-status`, { params: { month } })
    return response.data
  },
}
